/**
 * Password Generator View
 * Interactive password generation with options
 */
import React, { useState } from 'react';
import { Box, Text, useInput } from 'ink';
import TextInput from 'ink-text-input';
import { UtilsService } from '../../../services/utils.service.js';

export const PasswordView: React.FC = () => {
    const [length, setLength] = useState('16');
    const [count, setCount] = useState('1');
    const [passwords, setPasswords] = useState<string[]>([]);
    const [focusedField, setFocusedField] = useState<'length' | 'count' | 'generate'>('length');
    const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

    const service = new UtilsService();

    // Auto-generate on first mount
    React.useEffect(() => {
        handleGenerate();
    }, []);

    useInput((input, key) => {
        if (key.tab) {
            // Cycle through fields
            if (focusedField === 'length') setFocusedField('count');
            else if (focusedField === 'count') setFocusedField('generate');
            else setFocusedField('length');
        } else if (key.return) {
            // Generate passwords on Enter from any field
            handleGenerate();
        } else if (input === 'c' && passwords.length > 0) {
            // Copy first password
            handleCopy(0);
        }
    });

    const handleGenerate = () => {
        const newPasswords: string[] = [];
        const lengthNum = parseInt(length, 10) || 16;
        const countNum = parseInt(count, 10) || 1;

        for (let i = 0; i < countNum; i++) {
            newPasswords.push(
                service.generatePassword({
                    length: lengthNum,
                    lowercase: true,
                    uppercase: true,
                    digits: true,
                    symbols: true,
                })
            );
        }
        setPasswords(newPasswords);
        setCopiedIndex(null);
    };

    const handleCopy = async (index: number) => {
        try {
            const { default: clipboardy } = await import('clipboardy');
            await clipboardy.write(passwords[index]);
            setCopiedIndex(index);
            setTimeout(() => setCopiedIndex(null), 2000);
        } catch (error) {
            // Clipboard not available
        }
    };

    return (
        <Box flexDirection="column">
            <Box marginBottom={1}>
                <Text bold color="cyan">
                    🔐 Password Generator
                </Text>
            </Box>

            {/* Options */}
            <Box
                flexDirection="column"
                borderStyle="single"
                borderColor="gray"
                paddingX={2}
                paddingY={1}
                marginBottom={1}
            >
                <Text bold color="yellow" marginBottom={1}>
                    Options:
                </Text>

                {/* Length Input */}
                <Box marginBottom={1}>
                    <Box width={20}>
                        <Text color={focusedField === 'length' ? 'green' : undefined}>
                            {focusedField === 'length' ? '▶ ' : '  '}Length:
                        </Text>
                    </Box>
                    <Box width={10}>
                        {focusedField === 'length' ? (
                            <TextInput value={length} onChange={setLength} />
                        ) : (
                            <Text>{length}</Text>
                        )}
                    </Box>
                    <Text dimColor>(characters)</Text>
                </Box>

                {/* Count Input */}
                <Box marginBottom={1}>
                    <Box width={20}>
                        <Text color={focusedField === 'count' ? 'green' : undefined}>
                            {focusedField === 'count' ? '▶ ' : '  '}Count:
                        </Text>
                    </Box>
                    <Box width={10}>
                        {focusedField === 'count' ? (
                            <TextInput value={count} onChange={setCount} />
                        ) : (
                            <Text>{count}</Text>
                        )}
                    </Box>
                    <Text dimColor>(passwords to generate)</Text>
                </Box>

                {/* Generate Button */}
                <Box marginTop={1}>
                    <Text
                        bold
                        backgroundColor={focusedField === 'generate' ? 'green' : undefined}
                        color={focusedField === 'generate' ? 'black' : 'green'}
                    >
                        {focusedField === 'generate' ? '▶ ' : '  '}
                        [ Generate Passwords ]
                    </Text>
                </Box>
            </Box>

            {/* Results */}
            {passwords.length > 0 && (
                <Box
                    flexDirection="column"
                    borderStyle="single"
                    borderColor="green"
                    paddingX={2}
                    paddingY={1}
                >
                    <Text bold color="green" marginBottom={1}>
                        ✓ Generated Passwords:
                    </Text>

                    {passwords.map((password, index) => (
                        <Box key={index} marginBottom={0}>
                            <Box width={4}>
                                <Text dimColor>{index + 1}.</Text>
                            </Box>
                            <Box flexGrow={1}>
                                <Text>{password}</Text>
                            </Box>
                            {copiedIndex === index && (
                                <Text color="green"> ✓ Copied!</Text>
                            )}
                        </Box>
                    ))}
                </Box>
            )}

            {/* Help */}
            <Box marginTop={1} borderStyle="single" borderColor="gray" paddingX={2}>
                <Text dimColor>
                    Tab: Next field | Enter: Generate | C: Copy | Esc: Back
                </Text>
            </Box>
        </Box>
    );
};
